import React, { useLayoutEffect, useRef, useState } from 'react';

import { animated, config, useSpring } from 'react-spring';
import { makeStyles } from 'tss-react/mui';

const useStyles = makeStyles()({
  /**
   * 0fr ↔ 1fr is height:auto without measuring. Overflow stays off until
   * the spring rests so descendants (sticky titles, dropdowns) are not
   * clipped against the animating box.
   */
  clip: {
    display: 'grid',
  },
  clipInner: {
    minHeight: 0,
  },
  clipInnerClipping: {
    overflow: 'hidden',
  },
  clipping: {
    overflow: 'hidden',
  },
});

/**
 * Springs children between closed (0fr) and open (1fr). No pixel measure —
 * the grid row is the height.
 */
export function PlaybookAnimatedClip({
  children,
  clippingClassName,
  expanded,
}: {
  children: React.ReactNode;
  expanded: boolean;
  /** Extra class on the clip while closed or in flight. */
  clippingClassName?: string;
}): JSX.Element {
  const { classes, cx } = useStyles();
  const expandedRef = useRef(expanded);
  expandedRef.current = expanded;
  const [settledOpen, setSettledOpen] = useState(expanded);

  useLayoutEffect(() => {
    if (!expanded) setSettledOpen(false);
  }, [expanded]);

  const { open } = useSpring({
    config: { ...config.default, clamp: true },
    onRest: () => {
      if (expandedRef.current) setSettledOpen(true);
    },
    open: expanded ? 1 : 0,
  });

  return (
    <animated.div
      aria-hidden={!expanded}
      className={cx(
        classes.clip,
        !settledOpen && classes.clipping,
        !settledOpen && clippingClassName
      )}
      inert={!expanded ? true : undefined}
      style={{ gridTemplateRows: open.to(value => `${value}fr`) }}
    >
      <div className={cx(classes.clipInner, !settledOpen && classes.clipInnerClipping)}>
        {children}
      </div>
    </animated.div>
  );
}
